import { AuthProvider } from "@refinedev/core";
import { jwtAuthService, User } from "../services/jwtAuthService"; // Updated JWT service with token expiry fixes
import { authStateManager } from "../services/authStateManager"; // Persistent auth state

export interface AuthResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  user: User;
  tenant_slug?: string;
}

interface LoginParams {
  email: string;
  password: string;
  tenant_subdomain?: string;
}

export const authProvider: AuthProvider = {
  login: async ({ email, password, totp_code, passkey_assertion, rp_id, origin }: LoginParams & {
    totp_code?: string;
    // GateBOXX passkey (WebAuthn) step-up: on a require_passkey=required tenant
    // the login page resubmits carrying the navigator.credentials.get() result
    // plus the login host. All optional and inert while require_passkey=off.
    passkey_assertion?: unknown;
    rp_id?: string;
    origin?: string;
  }) => {
    const loginId = Math.random().toString(36).substr(2, 9);
    try {
      console.log(`🔐 [${loginId}] AuthProvider: Starting login process...`);
      console.log(`🔐 [${loginId}] Current URL:`, window.location.href);
      console.log(`🔐 [${loginId}] Email:`, email, 'Password length:', password.length);

      const passkey = (passkey_assertion && rp_id && origin)
        ? { assertion: passkey_assertion, rpId: rp_id, origin }
        : undefined;
      const loginResponse = await jwtAuthService.login(email, password, totp_code, passkey);
      console.log(`✅ [${loginId}] AuthProvider: Login service successful:`, loginResponse);
      console.log(`[${loginId}] AuthProvider: Tokens stored`);

      // Set persistent auth state
      authStateManager.setAuthenticated(true);

      // Set login timestamp to prevent immediate auth checks
      localStorage.setItem('last_successful_login', Date.now().toString());

      // No redirectTo on purpose: the login page owns navigation so it can run
      // the passkey ENROL ceremony (device_trust.passkey_enroll) BEFORE leaving
      // the page. device_trust rides back so it can decide.
      return {
        success: true,
        device_trust: loginResponse.device_trust,
      } as any;
    } catch (error: any) {
      console.error(`❌ [${loginId}] AuthProvider: Login failed:`, error);
      console.error(`❌ [${loginId}] Error details:`, {
        message: error.message,
        status: error.status,
        response: error.response,
        stack: error.stack
      });
      return {
        success: false,
        error: {
          name: "LoginError",
          message: error.message || "Invalid credentials. Please check your email and password.",
          // Surfaced to the login page so it can reveal the 2FA code field.
          require_2fa: !!error.require_2fa,
          // Device-trust enforce refusal: the login page shows this code for
          // the admin to approve the browser (Core Settings → Device Trust).
          device_not_trusted: !!error.device_not_trusted,
          pairing_code: error.pairing_code || '',
          // Refusal screen may ask the requester for name/phone/email so the
          // admin can call back before approving (console toggle).
          collect_contact: !!error.collect_contact,
          contact_required: !!error.contact_required,
          // GateBOXX passkey step-up: reason==='passkey_required' plus a
          // JSON-encoded WebAuthn challenge the login page runs .get() against.
          reason: error.reason || '',
          passkey_required: !!error.passkey_required,
          passkey_challenge: error.passkey_challenge ?? null,
        } as any,
      };
    }
  },

  logout: async () => {
    console.log('🚪 AuthProvider logout called - this should be user-initiated only');
    try {
      await jwtAuthService.logout();
      authStateManager.clearCache();
    } catch (error) {
      console.error("Logout error:", error);
    }

    return {
      success: true,
      redirectTo: "/login",
    };
  },

  check: async () => {
    const checkId = Math.random().toString(36).substr(2, 9);
    console.log(`🔍 [${checkId}] Auth check starting...`);
    console.log(`🔍 [${checkId}] Current URL:`, window.location.href);
    
    const rawAccessToken = localStorage.getItem('jwt_access_token');
    const rawRefreshToken = localStorage.getItem('jwt_refresh_token');
    const rawUser = localStorage.getItem('jwt_user');
    const tokenExpiry = localStorage.getItem('jwt_token_expiry');
    
    console.log(`🔍 [${checkId}] Token status:`, {
      accessToken: rawAccessToken ? 'EXISTS' : 'MISSING',
      refreshToken: rawRefreshToken ? 'EXISTS' : 'MISSING', 
      userData: rawUser ? 'EXISTS' : 'MISSING',
      expiry: tokenExpiry ? new Date(tokenExpiry).toLocaleString() : 'MISSING'
    });
    
    // If no tokens exist at all, redirect to login
    if (!rawAccessToken && !rawRefreshToken) {
      console.log(`❌ [${checkId}] No tokens found - redirecting to login`);
      return { 
        authenticated: false,
        redirectTo: "/login",
        error: {
          message: "Please login to continue",
          name: "AuthenticationRequired"
        }
      };
    }
    
    // Check if token is expired
    if (tokenExpiry) {
      const expiryDate = new Date(tokenExpiry);
      const now = new Date();
      
      if (now > expiryDate) {
        console.log(`⏰ [${checkId}] Token expired at ${expiryDate.toLocaleString()}`);
        
        // Try to refresh the token if we have a refresh token
        if (rawRefreshToken) {
          console.log(`� [${checkId}] Attempting token refresh...`);
          try {
            const refreshed = await jwtAuthService.attemptRefresh();
            if (refreshed) {
              console.log(`✅ [${checkId}] Token refreshed successfully`);
              return { authenticated: true };
            }
          } catch (error) {
            console.error(`❌ [${checkId}] Token refresh failed:`, error);
          }
        }
        
        // If refresh failed or no refresh token, redirect to login
        console.log(`❌ [${checkId}] Token expired and refresh failed - redirecting to login`);
        localStorage.clear(); // Clear all auth data
        return { 
          authenticated: false,
          redirectTo: "/login",
          error: {
            message: "Your session has expired. Please login again.",
            name: "SessionExpired"
          }
        };
      }
    }
    
    // Tokens exist and are valid
    console.log(`✅ [${checkId}] Authentication valid`);
    return { authenticated: true };
  },

  getPermissions: async () => {
    try {
      const user = await jwtAuthService.getUserProfile();
      // Extract permissions from user profile if available
      return (user as any).permissions || [];
    } catch (error) {
      console.error("Error fetching permissions:", error);
      return [];
    }
  },

  getIdentity: async () => {
    try {
      // First, try to get stored user data (faster and more reliable)
      const storedUser = jwtAuthService.getCurrentUser();
      if (storedUser) {
        console.log('✅ AuthProvider: getUserIdentity success from storage');
        return storedUser;
      }
    } catch (error) {
      console.error("Error fetching identity from storage:", error);
    }
    
    try {
      // Fallback: Try to get fresh user profile from API
      const user = await jwtAuthService.getUserProfile();
      if (user) {
        console.log('✅ AuthProvider: getUserIdentity success from API');
        return user;
      }
    } catch (error) {
      console.error("Error fetching identity from API:", error);
    }
    
    // Last fallback: return minimal user data to prevent undefined
    console.warn('⚠️ AuthProvider: No user data available, returning fallback');
    return {
      id: 'unknown',
      name: 'Unknown User',
      email: 'unknown@example.com',
      role: 'user'
    };
  },

  onError: async (error: any) => {
    // Handle errors gracefully
    return { error };
  },
};
